import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Calendar, ArrowLeft, Phone, Mail, ShieldCheck, Send, Hammer, ChevronRight } from 'lucide-react';
import { companyInfo } from '../data';
import useDocumentTitle from '../hooks/useDocumentTitle';

export default function ReferenceDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [otherProjects, setOtherProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useDocumentTitle(project?.title || 'Reference');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: `Hej Christian, jeg er interesseret i at høre mere om et projekt lignende "${project?.title || ''}".`,
    website_url: '' // Honeypot
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (project?.title) {
      setFormData(prev => ({
        ...prev,
        message: `Hej Christian, jeg er interesseret i at høre mere om et projekt lignende "${project.title}".`
      }));
    }
  }, [project]);

  useEffect(() => {
    setLoading(true);
    setError(false);
    setFormStatus({ type: '', message: '' });

    // Fetch the specific reference by slug
    fetch(`https://api.dev.auxo.dk/public/by-slug/da/${slug}`)
      .then((res) => {
        if (!res.ok) throw new Error('Projektet blev ikke fundet');
        return res.json();
      })
      .then((data) => {
        setProject(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching project:', err);
        setError(true);
        setLoading(false);
      });

    // Fetch other references for the bottom section
    fetch('https://api.dev.auxo.dk/public/content/referencer')
      .then((res) => res.json())
      .then((data) => {
        // Filter out the current project if possible (we do it by matching slug)
        const filtered = data.filter((item) => item.slug?.da !== slug).slice(0, 3);
        setOtherProjects(filtered);
      })
      .catch((err) => console.error('Error fetching other projects:', err));
  }, [slug]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setFormStatus({
          type: 'success',
          message: 'Tak! Jeg har modtaget din henvendelse og vender tilbage hurtigst muligt.',
        });
        setFormData({
          name: '',
          phone: '',
          email: '',
          message: `Hej Christian, jeg er interesseret i at høre mere om et projekt lignende "${project?.title}".`,
          website_url: ''
        });
      } else {
        throw new Error('Fejl ved indsendelse');
      }
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: 'Der skete en fejl. Ring venligst direkte på ' + companyInfo.phone,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-700 mb-4"></div>
        <p className="text-neutral-600 font-medium">Henter projektdetaljer...</p>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center py-20 px-4 text-center">
        <h2 className="text-3xl font-extrabold text-neutral-950 mb-4">Projektet blev ikke fundet</h2>
        <p className="text-neutral-600 max-w-md mb-8">
          Det ser ud til, at det projekt, du leder efter, enten er blevet flyttet eller ikke eksisterer.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 bg-red-700 hover:bg-red-800 text-white text-sm font-bold uppercase tracking-wider px-6 py-3 rounded-sm transition-colors"
        >
          <ArrowLeft size={16} />
          <span>Gå til forsiden</span>
        </Link>
      </div>
    );
  }

  const imageUrl = Array.isArray(project.image) ? project.image[0] : project.image;

  return (
    <div className="w-full bg-neutral-50">
      {/* Header / Breadcrumbs Section */}
      <section className="bg-neutral-950 text-white py-12 border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-2 text-xs text-neutral-400 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Forside</Link>
            <ChevronRight size={12} />
            <span className="text-neutral-200">Referencer</span>
            <ChevronRight size={12} />
            <span className="text-red-500 truncate max-w-[200px] sm:max-w-none">{project.title}</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="max-w-3xl">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight mt-2 text-white">
                {project.title}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content & Sidebar */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Column: Image & Description */}
            <div className="lg:col-span-8 space-y-8">
              {/* Main Project Image */}
              <div className="bg-neutral-900 rounded-sm overflow-hidden shadow-md border border-neutral-200/10">
                <img
                  src={imageUrl}
                  alt={project.title}
                  className="w-full h-auto object-cover max-h-[550px]"
                />
              </div>

              {/* Project Description */}
              <div className="bg-white p-8 rounded-sm shadow-sm border border-neutral-200/60 space-y-6">
                <h2 className="text-2xl font-extrabold text-neutral-950 tracking-tight border-b border-neutral-100 pb-4">
                  Om projektet
                </h2>
                <p className="text-neutral-700 text-base leading-relaxed whitespace-pre-line">
                  {project.description}
                </p>
                
                <div className="pt-6 border-t border-neutral-100">
                  <h3 className="text-lg font-bold text-neutral-950 mb-3 flex items-center gap-2">
                    <span>Fokus på kvalitet og murerdyder</span>
                  </h3>
                  <p className="text-neutral-600 text-sm leading-relaxed">
                    Hos Pedersen Byg udfører jeg alle projekter med øje for detaljen. Ved dette projekt i {project.location} har jeg haft særligt fokus på at vælge materialer, der harmonerer med de eksisterende rammer, og sikre en finish, der holder i mange år. Jeg rydder altid pænt op efter mig selv, så kunden kun skal glæde sig over det færdige resultat.
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column: Sticky Sidebar */}
            <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-24">
              {/* Project Facts Card */}
              <div className="bg-white p-6 rounded-sm shadow-sm border border-neutral-200/60">
                <h3 className="text-lg font-bold text-neutral-950 mb-4 pb-2 border-b border-neutral-100">
                  Projektfakta
                </h3>
                <ul className="space-y-4">
                  <li className="flex justify-between items-center text-sm py-1">
                    <span className="text-neutral-500">Udført af:</span>
                    <span className="font-bold text-neutral-950">{companyInfo.name}</span>
                  </li>
                  <li className="flex justify-between items-center text-sm py-1">
                    <span className="text-neutral-500">Lokation:</span>
                    <span className="font-bold text-neutral-950 flex items-center gap-1">
                      <MapPin size={14} className="text-red-700" />
                      {project.location}
                    </span>
                  </li>
                  <li className="flex justify-between items-center text-sm py-1">
                    <span className="text-neutral-500">Afleveret år:</span>
                    <span className="font-bold text-neutral-950">{project.year}</span>
                  </li>
                </ul>
              </div>

              {/* Contact Sidebar Card */}
              <div className="bg-neutral-950 text-white p-6 rounded-sm shadow-lg border border-neutral-800">
                <h3 className="text-lg font-bold text-white mb-1">
                  Interesseret i noget lignende?
                </h3>
                <p className="text-neutral-400 text-xs mb-6">
                  Udfyld formularen herunder, så kontakter Christian dig for en uforpligtende snak om dit murerprojekt.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Honeypot field - absolutely hidden from humans */}
                  <div className="sr-only absolute -top-[9999px] -left-[9999px]">
                    <label htmlFor="sidebar_website_url">Do not fill this out</label>
                    <input
                      type="text"
                      id="sidebar_website_url"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleInputChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1">
                      Navn *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full bg-neutral-900 border border-neutral-800 rounded-sm px-3 py-2 text-xs text-white focus:outline-none focus:border-red-500"
                      placeholder="Dit navn"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1">
                      Telefonnummer *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full bg-neutral-900 border border-neutral-800 rounded-sm px-3 py-2 text-xs text-white focus:outline-none focus:border-red-500"
                      placeholder="Tlf. nr."
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1">
                      E-mail *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full bg-neutral-900 border border-neutral-800 rounded-sm px-3 py-2 text-xs text-white focus:outline-none focus:border-red-500"
                      placeholder="din@email.dk"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1">
                      Besked
                    </label>
                    <textarea
                      name="message"
                      required
                      rows="3"
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full bg-neutral-900 border border-neutral-800 rounded-sm px-3 py-2 text-xs text-white focus:outline-none focus:border-red-500 resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-red-700 hover:bg-red-800 disabled:bg-neutral-700 text-white text-xs font-bold uppercase tracking-wider py-3 rounded-sm transition-all duration-200 hover:-translate-y-0.5 flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <Send size={12} />
                    <span>{submitting ? 'Sender...' : 'Send forespørgsel'}</span>
                  </button>

                  {formStatus.message && (
                    <div
                      className={`p-3 rounded-sm text-xs font-medium mt-3 ${
                        formStatus.type === 'success'
                          ? 'bg-emerald-950/50 border border-emerald-800 text-emerald-300'
                          : 'bg-red-950/50 border border-red-800 text-red-300'
                      }`}
                    >
                      {formStatus.message}
                    </div>
                  )}
                </form>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Related Projects Section */}
      {otherProjects.length > 0 && (
        <section className="bg-white text-neutral-950 py-16 border-t border-neutral-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-10">
              <span className="text-xs font-bold uppercase tracking-widest text-red-700">Inspiration</span>
              <h2 className="text-2xl md:text-3xl font-extrabold text-neutral-950 tracking-tight mt-1">
                Se andre udførte projekter
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {otherProjects.map((proj) => {
                const projImg = Array.isArray(proj.image) ? proj.image[0] : proj.image;
                return (
                  <Link
                    key={proj.id}
                    to={`/referencer/${proj.slug?.da}`}
                    className="bg-white border border-neutral-200/80 rounded-sm overflow-hidden shadow-md hover:shadow-xl hover:border-neutral-300 transition-all duration-300 flex flex-col group"
                  >
                    <div className="relative h-48 overflow-hidden bg-neutral-100">
                      <img
                        src={projImg}
                        alt={proj.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/50 via-transparent to-transparent" />
                      <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-1.5">
                        <span className="inline-flex items-center gap-1 bg-red-700 text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm">
                          <MapPin size={8} />
                          {proj.location}
                        </span>
                        <span className="inline-flex items-center gap-1 bg-white/90 text-neutral-800 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border border-neutral-200 shadow-sm">
                          <Calendar size={8} />
                          År {proj.year}
                        </span>
                      </div>
                    </div>
                    <div className="p-5 flex-grow flex flex-col justify-between">
                      <div>
                        <h3 className="text-base font-bold text-neutral-950 mb-2 line-clamp-1">
                          {proj.title}
                        </h3>
                        <p className="text-neutral-600 text-xs leading-relaxed line-clamp-2">
                          {proj.description}
                        </p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
